// Utilities
import { make } from 'vuex-pathify'

// Globals
import {
  DEFAULT_ACCENT_COLOR,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  IN_BROWSER,
  QQ_ACCENT_COLOR,
  QQ_PRIMARY_COLOR,
  QQ_SECONDARY_COLOR,
} from '@/util/globals'
import { readCookie } from '@/util/helpers'
import router from '@/router'
import moment from 'moment'
import { DataStore } from '@aws-amplify/datastore'
import { UserSettings } from '@/models'
import _ from 'lodash'

const state = {
  users: {
    sessionTime: null,
    authState: undefined,
    update_program: '',
    isAdmin: false,
    isEstimator: false,
    isGuest: false,
    isCustomer: false,
    allowStage: false,
    user: undefined,
    settings: null,
    default: {
      clients: [],
      client_id: null,
      estimator_id: null,
    },
    selectedColors: {
      primary: null,
      secondary: null,
      accent: null,
    },
  },
  dark: false,
  drawer: {
    image: 0,
    gradient: 0,
    mini: false,
  },
  gradients: [
    'rgba(0, 0, 0, .7), rgba(0, 0, 0, .7)',
    'rgba(228, 226, 226, 1), rgba(255, 255, 255, 0.7)',
    'rgba(244, 67, 54, .8), rgba(244, 67, 54, .8)',
  ],
  /*
  'https://demos.creative-tim.com/material-dashboard-pro/assets/img/sidebar-2.jpg',
  'https://demos.creative-tim.com/material-dashboard-pro/assets/img/sidebar-4.jpg',
   */
  images: [
    require('@/assets/BLUE-PRINT.jpg'),
    require('@/assets/blueprints-blue.png'),
    require('@/assets/blueprint-old-house.jpg'),
  ],
  notifications: [],
  rtl: false,
}

const mutations = {
  ...make.mutations(state),

  successfulLogin: () => {},

  setUserSettings: (state, userSettings) => {
    state.users.settings = userSettings
    state.users.default.clients = [...userSettings.customers]

    if (state.users.default.client_id == null) {
      state.users.default.client_id = userSettings.default_customer
    }

    state.dark = userSettings.dark
    state.drawer.image = userSettings.image
    state.drawer.gradient = userSettings.gradient

    // Set Primary Color
    let qqPrimaryColor = userSettings.primary_color
    if (qqPrimaryColor === null) {
      qqPrimaryColor = readCookie(QQ_PRIMARY_COLOR)
      if (!qqPrimaryColor) {
        qqPrimaryColor = DEFAULT_PRIMARY_COLOR
      }
    }
    state.users.selectedColors.primary = qqPrimaryColor

    // Set Secondary Color
    let qqSecondaryColor = userSettings.secondary_color
    if (qqSecondaryColor === null) {
      qqSecondaryColor = readCookie(QQ_SECONDARY_COLOR)
      if (!qqSecondaryColor) {
        qqSecondaryColor = DEFAULT_SECONDARY_COLOR
      }
    }
    state.users.selectedColors.secondary = qqSecondaryColor

    // Set Accent Color
    let qqAccentColor = userSettings.accent_color
    if (qqAccentColor === null) {
      qqAccentColor = readCookie(QQ_ACCENT_COLOR)
      if (!qqAccentColor) {
        qqAccentColor = DEFAULT_ACCENT_COLOR
      }
    }
    state.users.selectedColors.accent = qqAccentColor
  },

  updateTheme: (state, payload) => {
    state.users.selectedColors.primary = payload.primary
    state.users.selectedColors.secondary = payload.secondary
    state.users.selectedColors.accent = payload.accent
  },

  showToast: (state, payload) => {},

  invalidateSession: () => {},
}

const actions = {
  fetch: ({ commit }) => {
    const local = localStorage.getItem('vuetify@user') || '{}'
    const user = JSON.parse(local)

    for (const key in user) {
      commit(key, user[key])
    }

    if (user.dark === undefined) {
      commit('dark', window.matchMedia('(prefers-color-scheme: dark)'))
    }
  },

  update: ({ state }) => {
    if (!IN_BROWSER) return

    localStorage.setItem('vuetify@user', JSON.stringify(state))
  },

  showToast: ({ commit }, payload) => {
    commit('showToast', payload)
  },

  invalidateSession: async ({ commit, dispatch }) => {
    state.users.sessionTime = null
    state.users.authState = 'signedout'
    state.users.isAdmin = false
    state.users.isEstimator = false
    state.users.isGuest = false
    state.users.isCustomer = false
    state.users.allowStage = false
    state.users.user = undefined

    dispatch('customer/setEmpty', null, { root: true })

    localStorage.clear()

    await DataStore.clear()

    commit('invalidateSession')
  },

  setUserSettings: ({ commit, dispatch, rootState }, payload) => {
    commit('setUserSettings', payload)
  },

  getUserSettings: async ({ dispatch }) => {
    const models = await DataStore.query(UserSettings)
    const settings = models.filter(data => data.user_email === state.users.user.attributes.email)

    if (settings) {
      if (settings.length > 0) {
        dispatch('setUserSettings', settings[0])
      }
    }
  },

  successfulLogin: async ({ commit, dispatch }) => {
    commit('successfulLogin')
  },

  setAuth: async ({ commit, dispatch, rootGetters, rootState }, payload) => {
    const thisAction = 'Set Auth'
    const estimatorGroup = 'Estimator:'
    let groups = []

    state.users.user = undefined

    if (payload.authData) {
      state.users.user = { attributes: {} }
      state.users.user.attributes = payload.authData.attributes
      state.users.user.attributes.groups = payload.authData.signInUserSession.accessToken.payload['cognito:groups']
    }

    let sessionTime

    switch (payload.authState) {
      case 'signin':
        await DataStore.stop()
        break
      case 'signedin':
        await DataStore.start()

        groups = state.users.user.attributes.groups

        sessionTime = localStorage.getItem('vuetify@userSessionTime') || moment()
        localStorage.setItem('vuetify@userSessionTime', sessionTime)

        state.users.sessionTime = sessionTime
        state.users.isAdmin = false
        state.users.isEstimator = false
        state.users.isGuest = false
        state.users.isCustomer = false
        state.users.allowStage = false
        state.users.default.clients = []
        state.users.default.client_id = null
        state.users.default.estimator_id = null

        if (groups) {
          for (let i = 0; i < groups.length; i++) {
            switch (groups[i]) {
              case 'Administrators':
                state.users.isAdmin = true
                break
              case 'Estimators':
                state.users.isEstimator = true
                break
              case 'ServiceGuest':
                state.users.isGuest = true
                break
              case 'Customer':
                state.users.isCustomer = true
                break
              case 'AllowStage':
                state.users.allowStage = true
                break
              default:
                if (groups[i].startsWith(estimatorGroup)) {
                  state.users.default.estimator_id = groups[i].replace(estimatorGroup, '')
                } else {
                  console.info('Unhandled group: ', groups[i])
                }
                break
            }
          }

          if (rootState.app.build_environment === 'Stage') {
            if (!state.users.allowStage) {
              dispatch('user/showToast',
                {
                  title: 'Unauthorized',
                  messages: ['This user does not have permissions to this environment.'],
                  variant: 'error',
                  timeout: 6000,
                },
                { root: true },
              )

              dispatch('invalidateSession')

              return
            }
          }
        }

        state.users.authState = payload.authState

        if (router.currentRoute.path !== '/') await router.push('/')

        dispatch('successfulLogin')

        break
      case 'signedout':
        dispatch('invalidateSession')

        break
    }
  },

  setPageName: ({ commit, dispatch }, pageName) => {
    state.users.update_program = pageName
    // TODO: moment format deprecated [ tried .format('YYYYMMDDHHmmss'), .setDate(.getDate()), .toISOString() ]
    /*
    const threshold = moment(state.users.sessionTime).add(7, 'days')
    const dateNow = moment()

    if (threshold && dateNow && moment(dateNow).isAfter(threshold)) {
      dispatch('invalidateSession')
    }
     */
  },

  setStateAsUpdated: ({ commit, rootState }, payload) => {
    payload.updated = true
    payload.client_id = rootState.customer.customers.selectedItem.client_id
    payload.update_program = state.users.update_program
    payload.update_user_id = state.users.user.attributes.email
  },

  setPrimaryColor: async ({ commit }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.primary_color = payload
      }),
    )

    const newTheme = {
      primary: payload,
      secondary: state.users.selectedColors.secondary,
      accent: state.users.selectedColors.accent,
    }

    commit('updateTheme', newTheme)
  },

  setSecondaryColor: async ({ commit }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.secondary_color = payload
      }),
    )

    const newTheme = {
      primary: state.users.selectedColors.primary,
      secondary: payload,
      accent: state.users.selectedColors.accent,
    }

    commit('updateTheme', newTheme)
  },

  setAccentColor: async ({ commit }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.accent_color = payload
      }),
    )

    const newTheme = {
      primary: state.users.selectedColors.primary,
      secondary: state.users.selectedColors.secondary,
      accent: payload,
    }

    commit('updateTheme', newTheme)
  },

  setShowImage: async ({ commit, dispatch }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.show_image = payload
      }),
    )

    dispatch('getUserSettings')
  },

  setImage: async ({ commit, dispatch }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.image = payload.toString()
      }),
    )

    dispatch('getUserSettings')
  },

  setGradient: async ({ commit, dispatch }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.gradient = payload.toString()
      }),
    )

    dispatch('getUserSettings')
  },

  setDarkMode: async ({ commit, dispatch }, payload) => {
    const original = await DataStore.query(UserSettings, state.users.settings.id)

    await DataStore.save(
      UserSettings.copyOf(original, updated => {
        updated.dark = payload
      }),
    )

    dispatch('getUserSettings')
  },
}

const getters = {
  dark: (state, getters) => {
    return (
      state.dark ||
      getters.gradient.indexOf('255, 255, 255') === -1
    )
  },

  gradient: state => {
    let currentValue = state.gradients[0]

    if (state.users.settings) {
      currentValue = state.gradients[state.users.settings.gradient]
    }

    return currentValue
  },

  image: state => {
    let currentValue = state.images[0]

    if (state.users.settings) {
      currentValue = state.images[state.users.settings.image]
    }

    return currentValue
  },

  getAllowedCustomers: () => (payload) => {
    let customerData = []

    // TODO: We may want to require All and empty shows none to keep clients from seeing other clients
    if (_.isEmpty(state.users.default.clients)) {
      if (state.users.default !== null) {
        if ({}.hasOwnProperty.call(state.users.default, 'client_id')) {
          if (state.users.default.client_id !== null) {
            if (state.users.default.client_id.trim().length > 0) {
              customerData = payload
            }
          }
        }
      }
    } else {
      for (const currentData of payload) {
        if (state.users.default.clients.includes(currentData.client_id.toString())) {
          // push client to data
          customerData.push({ ...currentData })
        }
      }
    }

    return customerData
  },

  getEstimatorsData: () => (payload, defaultEstimator) => {
    let estimatorsData = []

    if (defaultEstimator) {
      for (const currentData of payload) {
        if (currentData.estimator_id) {
          if (currentData.estimator_id === defaultEstimator) {
            // push estimator's data
            estimatorsData.push({ ...currentData })
          }
        } else {
          // push unassigned or null estimator_id as well
          estimatorsData.push({ ...currentData })
        }
      }
    } else {
      estimatorsData = payload
    }

    return estimatorsData
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
